import { env } from '@logspot-platform/env/admin';

export const BASE_URL: string = env.VITE_APP_API_URL || '';
export const TRACK_URL: string = env.VITE_APP_TRACK_URL || '';

// Identify lives on the public-api at `/identify`, on the same origin as the
// track endpoint. Derive it from TRACK_URL (which ends in `/i` or `/track`),
// mirroring how the SDK derives `identifyApiUrl`. Dev: `/track` → `/identify`
// (proxied by Vite). Prod: `https://api…/i` → `https://api…/identify`.
const deriveIdentifyUrl = (trackUrl: string): string => {
  if (!trackUrl) return '/identify';
  return `${trackUrl.replace(/\/(i|track)\/?$/, '')}/identify`;
};
export const IDENTIFY_URL: string = deriveIdentifyUrl(TRACK_URL);

// Group lives on the public-api at `/group`, same origin/derivation as identify.
const deriveGroupUrl = (trackUrl: string): string => {
  if (!trackUrl) return '/group';
  return `${trackUrl.replace(/\/(i|track)\/?$/, '')}/group`;
};
export const GROUP_URL: string = deriveGroupUrl(TRACK_URL);

type RequestOptions = {
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // The backend sends error bodies two ways: a JSON envelope
    // (`{ status: 'ERROR', message }`) and, from the global error handler,
    // the raw `err.message` as plain text via `res.send()`. Read the body as
    // text first so both shapes surface the real message instead of a generic
    // "Request failed" (which hides the actual cause from the user).
    const raw = await response.text().catch(() => '');
    let message = raw;
    try {
      const parsed = JSON.parse(raw) as { message?: string };
      if (parsed?.message) message = parsed.message;
    } catch {
      // Not JSON — keep the plain-text body as the message.
    }
    throw new ApiError(
      response.status,
      message || `Request failed (${response.status})`,
    );
  }

  return response.json();
}

function buildHeaders(options?: RequestOptions): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  return headers;
}

export const api = {
  get: async <T>(path: string, options?: RequestOptions): Promise<T> => {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'GET',
      headers: buildHeaders(options),
      credentials: 'include',
      signal: options?.signal,
    });
    return handleResponse<T>(response);
  },

  post: async <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: buildHeaders(options),
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
    return handleResponse<T>(response);
  },

  put: async <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'PUT',
      headers: buildHeaders(options),
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
    return handleResponse<T>(response);
  },

  patch: async <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'PATCH',
      headers: buildHeaders(options),
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
    return handleResponse<T>(response);
  },

  delete: async <T>(path: string, options?: RequestOptions): Promise<T> => {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'DELETE',
      headers: buildHeaders(options),
      credentials: 'include',
      signal: options?.signal,
    });
    return handleResponse<T>(response);
  },
};

// The full set of fields the public `/track` endpoint accepts. The playground
// is the only caller, so this mirrors the ingestion contract directly: custom
// events, pageviews (name === "Pageview" + url), and revenue (value/currency)
// all flow through here, plus optional group association.
export type TrackEventInput = {
  name: string;
  user_id?: string;
  anonymous_id?: string;
  message?: string;
  metadata?: Record<string, unknown>;
  notify?: boolean;
  url?: string;
  hostname?: string;
  // Attribution signals the ingest pipeline reads for first/last-touch
  // (utm_* ride in `metadata`). The playground sets these to test attribution.
  referrer?: string;
  channel?: string;
  value?: number;
  currency?: string;
  external_id?: string;
  group_id?: string;
  group_type?: string;
};

export const trackApi = {
  sendEvent: async (
    publicKey: string,
    event: TrackEventInput,
  ): Promise<void> => {
    const response = await fetch(TRACK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-logspot-pk': `pk_${publicKey}`,
      },
      body: JSON.stringify(event),
    });
    if (!response.ok) {
      throw new ApiError(response.status, 'Failed to send event');
    }
  },
};

export const groupApi = {
  // Segment-style group() — associate a user/anon with a group (default type
  // "company"). POSTs to the public `/group` endpoint with the public key.
  sendGroup: async (
    publicKey: string,
    body: {
      group_id: string;
      user_id?: string;
      anonymous_id?: string;
      type?: string;
      traits?: Record<string, unknown>;
    },
  ): Promise<void> => {
    const response = await fetch(GROUP_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-logspot-pk': `pk_${publicKey}`,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ message: 'Failed to send group' }));
      throw new ApiError(
        response.status,
        err?.message || 'Failed to send group',
      );
    }
  },
};

export const identifyApi = {
  sendIdentify: async (
    publicKey: string,
    body: {
      user_id: string;
      anonymous_id?: string;
      traits?: Record<string, unknown>;
    },
  ): Promise<void> => {
    const response = await fetch(IDENTIFY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-logspot-pk': `pk_${publicKey}`,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ message: 'Failed to identify' }));
      throw new ApiError(response.status, err?.message || 'Failed to identify');
    }
  },
};
